// critical.js - Essential functions that must load immediately
// SCSS import removed (handled by separate CSS build)

/**
 * Site Configuration - Critical functions only
 */
const SITE_CONFIG = {
    // Only critical functions that need immediate loading
    initFunctions: [
        'simpleInfoModal',
        'professionalModal',
        'successModal',
        'navigationEnhancements'
    ],
    
    // API Configuration
    api: {
        baseUrl: 'https://api.travelhealthcare.com',
        endpoints: {
            contact: '/api/contact',
            jobs: '/api/jobs'
        }
    },
    
    // Feature flags - critical features only
    features: {
        modalEnabled: true,
        debugMode: false
    }
};

/**
 * Cookie Consent Policy - MOVED TO TOP FOR PRIORITY
 */
class CookieConsent {
    constructor() {
        this.consentKey = 'cookiesAccepted';
        this.gaId = 'G-3RB85694NS';
        this.init();
    }

    init() {
        if (localStorage.getItem(this.consentKey) === 'true') {
            this.loadGA4();
        } else {
            this.showBanner();
        }
    }

    loadGA4() {
        const script = document.createElement('script');
        script.src = `https://www.googletagmanager.com/gtag/js?id=${this.gaId}`;
        script.async = true;
        document.head.appendChild(script);

        window.dataLayer = window.dataLayer || [];
        function gtag() { dataLayer.push(arguments); }

        // P4 health-data lockout (WA MHMDA / FTC HBNR): only allowlisted, non-PII
        // params may reach GA4. Any event param NOT on this list (specialty, license,
        // email, phone, name, health fields, free text…) is dropped before dispatch.
        // 'config'/'js'/'consent' calls pass through untouched. Default-deny.
        const ALLOWED_EVENT_PARAMS = [
            'event_category', 'event_label', 'value', 'currency', 'items',
            'page_path', 'page_location', 'page_title', 'page_referrer',
            'method', 'content_type', 'link_url', 'engagement_time_msec',
            'send_to', 'non_interaction',
        ];
        window.gtag = function () {
            const cmd = arguments[0], target = arguments[1], params = arguments[2];
            if (cmd === 'event' && params && typeof params === 'object') {
                const clean = {};
                Object.keys(params).forEach(function (k) {
                    if (ALLOWED_EVENT_PARAMS.indexOf(k) !== -1) { clean[k] = params[k]; }
                });
                return gtag('event', target, clean);
            }
            return gtag.apply(null, arguments);
        };

        window.gtag('js', new Date());
        window.gtag('config', this.gaId);
    }

    showBanner() {
        const banner = document.createElement('div');
        banner.id = 'cookie-consent-banner';
        banner.className = 'cookie-banner shadow-lg';
        banner.innerHTML = `
            <div class="container d-flex flex-column flex-md-row align-items-center justify-content-between py-3">
                <div class="text-white mb-2 mb-md-0">
                    We use cookies to improve your experience and analyze traffic.
                    <a href="/privacy-policy" class="text-white text-decoration-underline">Learn more</a>.
                </div>
                <button id="accept-cookies" class="btn btn-primary ms-md-3">Accept</button>
            </div>
        `;
        document.body.appendChild(banner);

        // Direct event listener - not in a callback
        const acceptButton = document.getElementById('accept-cookies');
        acceptButton.addEventListener('click', () => {
            localStorage.setItem(this.consentKey, 'true');
            banner.remove();
            this.loadGA4();
        });
    }
}

/**
 * Simple Info Modal Class (for basic contact forms)
 */
class SimpleInfoModal {
    constructor() {
        if (!SITE_CONFIG.features.modalEnabled) return;

        this.modalHtml = `
            <div class="modal-overlay" id="simpleInfoModalOverlay" style="display: none;">
                <div class="modal">
                    <button class="modal-close" type="button">&times;</button>
                    <h2 id="simpleModalTitle">Partner With Us</h2>
                    <p class="subtitle">Let's discuss how we can help meet your staffing needs</p>
                    
                    <form id="simpleInfoForm">
                        <div class="form-section">
                            <h3>👤 Contact Information</h3>
                            <div class="form-row">
                                <div class="form-group">
                                    <label for="simpleFirstName">First Name <span class="required">*</span></label>
                                    <input type="text" id="simpleFirstName" name="firstName" required placeholder="John">
                                </div>
                                <div class="form-group">
                                    <label for="simpleLastName">Last Name <span class="required">*</span></label>
                                    <input type="text" id="simpleLastName" name="lastName" required placeholder="Smith">
                                </div>
                            </div>
                            <div class="form-row">
                                <div class="form-group">
                                    <label for="simpleEmail">Email Address <span class="required">*</span></label>
                                    <input type="email" id="simpleEmail" name="email" required placeholder="john.smith@email.com">
                                </div>
                                <div class="form-group phone-input">
                                    <label for="simplePhone">Phone Number <span class="required">*</span></label>
                                    <span class="phone-prefix">+1</span>
                                    <input type="tel" id="simplePhone" name="phone" required placeholder="(555) 123-4567">
                                </div>
                            </div>
                        </div>

                        <div class="form-section">
                            <h3>🏥 Organization Details</h3>
                            <div class="form-row">
                                <div class="form-group">
                                    <label for="simpleTitle">Your Title <span class="required">*</span></label>
                                    <input type="text" id="simpleTitle" name="title" required placeholder="HR Director, Nursing Manager, etc.">
                                </div>
                                <div class="form-group">
                                    <label for="simpleCompany">Organization/Facility <span class="required">*</span></label>
                                    <input type="text" id="simpleCompany" name="company" required placeholder="Hospital/Clinic Name">
                                </div>
                            </div>
                        </div>

                        <div class="success-message" id="simpleSuccessMessage" style="display: none;">
                            ✅ Thank you! We'll be in touch within 24 hours to discuss your staffing needs.
                        </div>
                        <div class="error-message" id="simpleErrorMessage" style="display: none;">
                            ❌ There was an error submitting your info.
                        </div>
                        <button type="submit" class="submit-button" id="simpleSubmitButton">
                            <span class="loading-spinner" id="simpleLoadingSpinner" style="display: none;"></span>
                            <span id="simpleButtonText">Get Started</span>
                        </button>
                    </form>
                </div>
            </div>
        `;
        this.apiUrl = `${SITE_CONFIG.api.baseUrl}${SITE_CONFIG.api.endpoints.contact}`;
        this.init();
    }

    setupTriggerListeners() {
        document.addEventListener('click', (e) => {
            if (e.target.matches('.open-simple-modal, .simple-modal-trigger')) {
                e.preventDefault();
                const titleText = e.target.dataset.modalTitle || 'Contact Form';
                this.open(titleText);
            } else {
                const triggerParent = e.target.closest('.open-simple-modal, .simple-modal-trigger');
                if (triggerParent && triggerParent !== e.target) {
                    e.preventDefault();
                    const titleText = triggerParent.dataset.modalTitle || 'Contact Form';
                    this.open(titleText);
                }
            }
        });
    }

    init() {
        document.body.insertAdjacentHTML('beforeend', this.modalHtml);
        this.setupEventListeners();
        this.setupTriggerListeners();
    }

    setupEventListeners() {
        // Form submission
        document.getElementById('simpleInfoForm').addEventListener('submit', (e) => {
            this.handleSubmit(e);
        });

        // Close modal when clicking overlay
        document.getElementById('simpleInfoModalOverlay').addEventListener('click', (e) => {
            if (e.target === e.currentTarget) {
                this.close();
            }
        });

        // Modal close button
        document.querySelector('#simpleInfoModalOverlay .modal-close').addEventListener('click', () => {
            this.close();
        });

        // Escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && document.getElementById('simpleInfoModalOverlay').style.display !== 'none') {
                this.close();
            }
        });

        // Phone formatting
        const phoneInput = document.getElementById('simplePhone');
        phoneInput.addEventListener('input', (e) => {
            this.formatPhoneNumber(e.target);
        });
    }

    formatPhoneNumber(input) {
        const value = input.value.replace(/\D/g, '');
        const match = value.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
        
        if (match) {
            let formattedValue = '';
            if (match[1]) {
                formattedValue = `(${match[1]}`;
                if (match[2]) {
                    formattedValue += `) ${match[2]}`;
                    if (match[3]) {
                        formattedValue += `-${match[3]}`;
                    }
                }
            }
            input.value = formattedValue;
        }
    }

    open(titleText) {
        const modal = document.getElementById('simpleInfoModalOverlay');
        const title = document.getElementById('simpleModalTitle');
        if (titleText) {
            title.textContent = titleText;
        }
        modal.style.display = 'flex';
        setTimeout(() => {
            modal.classList.add('active');
        }, 10);
        document.body.classList.add('modal-open');
    }

    close() {
        const modal = document.getElementById('simpleInfoModalOverlay');
        modal.style.display = 'none';
        modal.classList.remove('active');
        document.body.classList.remove('modal-open');
        this.resetForm();
    }

    resetForm() {
        document.getElementById('simpleInfoForm').reset();
        document.getElementById('simpleSuccessMessage').style.display = 'none';
        document.getElementById('simpleErrorMessage').style.display = 'none';
        document.getElementById('simpleSubmitButton').disabled = false;
        document.getElementById('simpleButtonText').textContent = 'Get Started';
    }

    async handleSubmit(e) {
        e.preventDefault();
        
        const submitButton = document.getElementById('simpleSubmitButton');
        const buttonText = document.getElementById('simpleButtonText');
        const loadingSpinner = document.getElementById('simpleLoadingSpinner');
        const successMessage = document.getElementById('simpleSuccessMessage');
        const errorMessage = document.getElementById('simpleErrorMessage');
        
        // Start loading state
        submitButton.disabled = true;
        buttonText.textContent = 'Submitting...';
        loadingSpinner.style.display = 'inline-block';
        errorMessage.style.display = 'none';
        
        try {
            const formData = {
                firstName: document.getElementById('simpleFirstName').value,
                lastName: document.getElementById('simpleLastName').value,
                email: document.getElementById('simpleEmail').value,
                phone: document.getElementById('simplePhone').value,
                title: document.getElementById('simpleTitle').value,
                company: document.getElementById('simpleCompany').value,
                timestamp: new Date().toISOString()
            };

            const response = await fetch(this.apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData)
            });

            const result = await response.json();

            if (result.success) {
                successMessage.style.display = 'block';
                buttonText.textContent = 'Submitted!';
                
                setTimeout(() => {
                    this.close();
                }, 3000);
            } else {
                throw new Error(result.message || 'Submission failed');
            }
        } catch (error) {
            console.error('Error:', error);
            errorMessage.style.display = 'block';
            buttonText.textContent = 'Try Again';
            submitButton.disabled = false;
        } finally {
            loadingSpinner.style.display = 'none';
        }
    }
}

/**
 * Professional Lead Modal Class (Complex modal with file uploads)
 */
class ProfessionalLeadModal {
    constructor() {
        this.apiUrl = 'https://api.travelhealthcare.com/api/contact';
        // P2 consent. Version + disclosure text are the single source for both the
        // rendered labels and the disclosure_hash, so the hash always matches what
        // was shown. Copy is counsel-approved (2026-07-31); any wording change must
        // bump consentVersion so the disclosure_hash provenance trail stays coherent.
        this.consentVersion = 'p2-2026-07-31';
        this.consentText = {
            collection: 'I consent to Travel Healthcare collecting my health-related information (specialty, license, and details I provide) to match me with roles.',
            share: 'I consent to Travel Healthcare sharing my information with healthcare recruiters and employers so they can contact me about opportunities.',
            tcpa: 'I agree to be contacted by Travel Healthcare and its recruiters by phone call, text/SMS, and email, including via automated technology. Consent is not a condition of any service; message and data rates may apply.',
        };
        this.modalHtml = `
            <div class="modal-overlay" id="professionalModalOverlay" style="display: none;">
                <div class="modal">
                    <button class="modal-close" type="button">&times;</button>
                    <h2 id="modalTitle">Join Our Professional Network</h2>
                    <p class="subtitle">Tell us about yourself and we'll connect you with the right opportunities</p>
                    
                    <form id="professionalLeadForm">
                        <input type="hidden" id="jobId" name="jobId" value="">
                        <div class="form-section">
                            <h3>👤 Personal Information</h3>
                            <div class="form-row">
                                <div class="form-group">
                                    <label for="firstName">First Name <span class="required">*</span></label>
                                    <input type="text" id="firstName" name="firstName" required placeholder="John">
                                </div>
                                <div class="form-group">
                                    <label for="lastName">Last Name <span class="required">*</span></label>
                                    <input type="text" id="lastName" name="lastName" required placeholder="Smith">
                                </div>
                            </div>

                            <div class="form-row">
                                <div class="form-group">
                                    <label for="email">Email Address <span class="required">*</span></label>
                                    <input type="email" id="email" name="email" required placeholder="john.smith@email.com">
                                </div>
                                <div class="form-group phone-input">
                                    <label for="phone">Phone Number <span class="required">*</span></label>
                                    <span class="phone-prefix">+1</span>
                                    <input type="tel" id="phone" name="phone" required placeholder="(555) 123-4567">
                                </div>
                            </div>
                        </div>

                        <div class="form-section">
                            <h3>💼 Professional Information</h3>
                            <div class="form-group">
                                <label for="specialty">Specialty/Field <span class="required">*</span></label>
                                <select id="modal-specialty" name="specialty" required>
                                    <option value="">Select your specialty</option>
                                    <option value="Registered Nurse">Registered Nurse</option>
                                    <option value="Licensed Practical Nurse">Licensed Practical Nurse</option>
                                    <option value="Certified Nursing Assistant">Certified Nursing Assistant</option>
                                    <option value="Physical Therapist">Physical Therapist</option>
                                    <option value="Occupational Therapist">Occupational Therapist</option>
                                    <option value="Speech Language Pathologist">Speech Language Pathologist</option>
                                    <option value="Respiratory Therapist">Respiratory Therapist</option>
                                    <option value="Medical Technologist">Medical Technologist</option>
                                    <option value="Radiology Technician">Radiology Technician</option>
                                    <option value="Pharmacy Technician">Pharmacy Technician</option>
                                    <option value="Healthcare Administration">Healthcare Administration</option>
                                    <option value="Social Worker">Social Worker</option>
                                    <option value="Other Healthcare Professional">Other Healthcare Professional</option>
                                </select>
                            </div>

                            <div class="form-row">
                                <div class="form-group">
                                    <label for="timeAvailable">Best Time to Contact</label>
                                    <select id="timeAvailable" name="timeAvailable">
                                        <option value="">Select preferred time</option>
                                        <option value="Early Morning (6AM-9AM)">Early Morning (6AM-9AM)</option>
                                        <option value="Morning (9AM-12PM)">Morning (9AM-12PM)</option>
                                        <option value="Afternoon (12PM-5PM)">Afternoon (12PM-5PM)</option>
                                        <option value="Evening (5PM-8PM)">Evening (5PM-8PM)</option>
                                        <option value="Weekends Only">Weekends Only</option>
                                        <option value="Anytime">Anytime</option>
                                    </select>
                                </div>
                                <div class="form-group">
                                    <label for="locationPreference">Location Preference</label>
                                    <select id="locationPreference" name="locationPreference">
                                        <option value="">Select work location preference</option>
                                        <option value="Local Only (within 25 miles)">Local Only (within 25 miles)</option>
                                        <option value="Regional (within 50 miles)">Regional (within 50 miles)</option>
                                        <option value="State-wide">State-wide</option>
                                        <option value="Travel Assignments">Travel Assignments</option>
                                        <option value="Remote Work">Remote Work</option>
                                        <option value="Flexible/Open to All">Flexible/Open to All</option>
                                    </select>
                                </div>
                            </div>

                            <div class="form-group">
                                <label for="stateLicLocation">State License Location <span class="required">*</span></label>
                                <select id="stateLicLocation" name="stateLicLocation" required>
                                    <option value="">Select state where you're licensed</option>
                                    <option value="Alabama">Alabama</option>
                                    <option value="Alaska">Alaska</option>
                                    <option value="Arizona">Arizona</option>
                                    <option value="Arkansas">Arkansas</option>
                                    <option value="California">California</option>
                                    <option value="Colorado">Colorado</option>
                                    <option value="Connecticut">Connecticut</option>
                                    <option value="Delaware">Delaware</option>
                                    <option value="Florida">Florida</option>
                                    <option value="Georgia">Georgia</option>
                                    <option value="Hawaii">Hawaii</option>
                                    <option value="Idaho">Idaho</option>
                                    <option value="Illinois">Illinois</option>
                                    <option value="Indiana">Indiana</option>
                                    <option value="Iowa">Iowa</option>
                                    <option value="Kansas">Kansas</option>
                                    <option value="Kentucky">Kentucky</option>
                                    <option value="Louisiana">Louisiana</option>
                                    <option value="Maine">Maine</option>
                                    <option value="Maryland">Maryland</option>
                                    <option value="Massachusetts">Massachusetts</option>
                                    <option value="Michigan">Michigan</option>
                                    <option value="Minnesota">Minnesota</option>
                                    <option value="Mississippi">Mississippi</option>
                                    <option value="Missouri">Missouri</option>
                                    <option value="Montana">Montana</option>
                                    <option value="Nebraska">Nebraska</option>
                                    <option value="Nevada">Nevada</option>
                                    <option value="New Hampshire">New Hampshire</option>
                                    <option value="New Jersey">New Jersey</option>
                                    <option value="New Mexico">New Mexico</option>
                                    <option value="New York">New York</option>
                                    <option value="North Carolina">North Carolina</option>
                                    <option value="North Dakota">North Dakota</option>
                                    <option value="Ohio">Ohio</option>
                                    <option value="Oklahoma">Oklahoma</option>
                                    <option value="Oregon">Oregon</option>
                                    <option value="Pennsylvania">Pennsylvania</option>
                                    <option value="Rhode Island">Rhode Island</option>
                                    <option value="South Carolina">South Carolina</option>
                                    <option value="South Dakota">South Dakota</option>
                                    <option value="Tennessee">Tennessee</option>
                                    <option value="Texas">Texas</option>
                                    <option value="Utah">Utah</option>
                                    <option value="Vermont">Vermont</option>
                                    <option value="Virginia">Virginia</option>
                                    <option value="Washington">Washington</option>
                                    <option value="West Virginia">West Virginia</option>
                                    <option value="Wisconsin">Wisconsin</option>
                                    <option value="Wyoming">Wyoming</option>
                                    <option value="District of Columbia">District of Columbia</option>
                                    <option value="Multiple States">Multiple States</option>
                                    <option value="Compact License">Compact License</option>
                                </select>
                            </div>

                            <!-- Resume Upload Section -->
                            <div class="form-group">
                                <label for="resume">Resume/CV</label>
                                <div class="file-upload-wrapper">
                                    <input 
                                        type="file" 
                                        id="resume" 
                                        name="resume" 
                                        accept=".pdf,.doc,.docx"
                                        class="file-input"
                                    >
                                    <label for="resume" class="file-upload-label">
                                        <span class="file-upload-icon">📄</span>
                                        <span class="file-upload-text">Choose file or drag here</span>
                                        <span class="file-upload-subtext">PDF, DOC, DOCX (Max 5MB)</span>
                                    </label>
                                    <div class="file-info" id="fileInfo" style="display: none;">
                                        <span class="file-name" id="fileName"></span>
                                        <button type="button" class="file-remove" id="fileRemove">×</button>
                                    </div>
                                </div>
                                <span class="file-error" id="fileError" style="display: none;"></span>
                            </div>
                        </div>

                        <div class="success-message" id="successMessage" style="display: none;">
                            ✅ Thank you! Your information has been submitted successfully. We'll review your profile and contact you soon.
                        </div>

                        <div class="error-message" id="errorMessage" style="display: none;">
                            ❌ There was an error submitting your information. Please check your details and try again.
                        </div>

                        <div class="form-section consent-section">
                            <div class="consent-group">
                                <label class="consent-check">
                                    <input type="checkbox" id="consentHealthCollection">
                                    <span>${this.consentText.collection} <span class="required">*</span></span>
                                </label>
                            </div>
                            <div class="consent-group">
                                ${navigator.globalPrivacyControl ? '<p class="gpc-notice">Your browser is set to opt out of sharing (Global Privacy Control), which we honor by default. To let recruiters and employers contact you about roles, check the box below.</p>' : ''}
                                <label class="consent-check">
                                    <input type="checkbox" id="consentHealthShare">
                                    <span>${this.consentText.share}</span>
                                </label>
                            </div>
                            <div class="consent-group">
                                <label class="consent-check">
                                    <input type="checkbox" id="consentTcpa">
                                    <span>${this.consentText.tcpa} <span class="required">*</span></span>
                                </label>
                            </div>
                        </div>

                        <button type="submit" class="submit-button" id="submitButton">
                            <span class="loading-spinner" id="loadingSpinner" style="display: none;"></span>
                            <span id="buttonText">Submit Application</span>
                        </button>
                    </form>
                </div>
            </div>
        `;
        
        this.jobId = null;
        this.currentCTA = '';
        this.selectedFile = null;
        this.maxFileSize = 5 * 1024 * 1024; // 5MB
        this.allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        
        this.init();
    }

    init() {
        document.body.insertAdjacentHTML('beforeend', this.modalHtml);
        this.setupEventListeners();
        this.setupTriggerListeners();
    }

    setupTriggerListeners() {
        document.addEventListener('click', (e) => {
            if (e.target.matches('.open-professional-modal, .professional-modal-trigger')) {
                e.preventDefault();
                const titleText = e.target.dataset.modalTitle || 'Contact Form';
                this.open(titleText);
            }
            const triggerParent = e.target.closest('.open-professional-modal, .professional-modal-trigger');
            if (triggerParent && triggerParent !== e.target) {
                e.preventDefault();
                const titleText = triggerParent.dataset.modalTitle || 'Contact Form';
                this.open(titleText);
            }
        });
    }

    setupEventListeners() {
        // Form submission
        document.getElementById('professionalLeadForm').addEventListener('submit', (e) => {
            this.handleSubmit(e);
        });

        // Close modal when clicking overlay
        document.getElementById('professionalModalOverlay').addEventListener('click', (e) => {
            if (e.target === e.currentTarget) {
                this.close();
            }
        });

        // Modal close button
        document.querySelector('#professionalModalOverlay .modal-close').addEventListener('click', () => {
            this.close();
        });

        // Close modal with Escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && document.getElementById('professionalModalOverlay').style.display !== 'none') {
                this.close();
            }
        });

        // Phone number formatting
        document.getElementById('phone').addEventListener('input', function() {
            let value = this.value.replace(/\D/g, '');
            if (value.length >= 6) {
                value = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`;
            } else if (value.length >= 3) {
                value = `(${value.slice(0, 3)}) ${value.slice(3)}`;
            }
            this.value = value;
        });

        // File upload handling
        const fileInput = document.getElementById('resume');
        const fileUploadLabel = document.querySelector('.file-upload-label');
        const fileInfo = document.getElementById('fileInfo');
        const fileName = document.getElementById('fileName');
        const fileRemove = document.getElementById('fileRemove');
        const fileError = document.getElementById('fileError');

        fileInput.addEventListener('change', (e) => {
            this.handleFileUpload(e.target.files[0]);
        });

        // Drag and drop
        ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
            fileUploadLabel.addEventListener(eventName, (e) => {
                e.preventDefault();
                e.stopPropagation();
            });
        });

        fileUploadLabel.addEventListener('drop', (e) => {
            const dt = e.dataTransfer;
            const files = dt.files;
            this.handleFileUpload(files[0]);
        });

        fileRemove.addEventListener('click', () => {
            this.removeFile();
        });
    }

    handleFileUpload(file) {
        const fileInfo = document.getElementById('fileInfo');
        const fileName = document.getElementById('fileName');
        const fileError = document.getElementById('fileError');
        const fileUploadLabel = document.querySelector('.file-upload-label');

        if (!file) return;

        // Reset error
        fileError.style.display = 'none';

        // Validate file
        if (!this.allowedTypes.includes(file.type)) {
            fileError.textContent = 'Please upload a PDF, DOC, or DOCX file';
            fileError.style.display = 'block';
            return;
        }

        if (file.size > this.maxFileSize) {
            fileError.textContent = 'File size must be less than 5MB';
            fileError.style.display = 'block';
            return;
        }

        // Store file reference
        this.selectedFile = file;

        // Update UI
        fileName.textContent = file.name;
        fileInfo.style.display = 'block';
        fileUploadLabel.style.display = 'none';
    }

    removeFile() {
        this.selectedFile = null;
        document.getElementById('resume').value = '';
        document.getElementById('fileInfo').style.display = 'none';
        document.querySelector('.file-upload-label').style.display = 'flex';
        document.getElementById('fileError').style.display = 'none';
    }

    open(cta) {
        const modal = document.getElementById('professionalModalOverlay');
        this.currentCTA = cta || '';
        
        // Update title if provided
        if (cta) {
            document.getElementById('modalTitle').textContent = cta;
        }
        
        // Check for job application data
        try {
            const jobData = sessionStorage.getItem('currentJobApplication');
            if (jobData) {
                const job = JSON.parse(jobData);
                this.jobId = job.jobId;
                document.getElementById('jobId').value = job.jobId;
                // Remove everything else
                sessionStorage.removeItem('currentJobApplication');
            }
        } catch (e) {
            console.warn('Could not retrieve job application data', e);
        }
        
        modal.style.display = 'flex';
        document.body.style.overflow = 'hidden';
        
        setTimeout(() => {
            modal.classList.add('active');
            document.getElementById('firstName').focus();
        }, 10);
    }

    close() {
        const modal = document.getElementById('professionalModalOverlay');
        modal.classList.remove('active');
        document.body.style.overflow = 'auto';
        
        setTimeout(() => {
            modal.style.display = 'none';
            this.resetForm();
        }, 300);
    }

    resetForm() {
        document.getElementById('professionalLeadForm').reset();
        document.getElementById('successMessage').style.display = 'none';
        document.getElementById('errorMessage').style.display = 'none';
        document.getElementById('submitButton').disabled = false;
        document.getElementById('buttonText').textContent = 'Submit Application';
        document.getElementById('loadingSpinner').style.display = 'none';
        this.removeFile();
        
        this.jobId = null;
        
        const jobIdField = document.querySelector('input[name="jobId"]');
        if (jobIdField) {
            jobIdField.remove();
        }
    }

    // SHA-256 hex of the exact disclosure text shown, so the stored hash proves
    // which wording the user agreed to. Requires a secure context (https).
    async computeConsentHash() {
        const canonical = [this.consentText.collection, this.consentText.share, this.consentText.tcpa].join('\n');
        const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(canonical));
        return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
    }

    async handleSubmit(e) {
        e.preventDefault();

        const submitButton = document.getElementById('submitButton');
        const buttonText = document.getElementById('buttonText');
        const loadingSpinner = document.getElementById('loadingSpinner');
        const successMessage = document.getElementById('successMessage');
        const errorMessage = document.getElementById('errorMessage');

        submitButton.disabled = true;
        buttonText.textContent = 'Submitting...';
        loadingSpinner.style.display = 'inline-block';
        successMessage.style.display = 'none';
        errorMessage.style.display = 'none';

        // P2 consent. health-collection + TCPA are required; share is optional.
        const healthCollectionConsent = document.getElementById('consentHealthCollection').checked;
        const healthShareConsent = document.getElementById('consentHealthShare').checked;
        const tcpaContactConsent = document.getElementById('consentTcpa').checked;
        if (!healthCollectionConsent || !tcpaContactConsent) {
            errorMessage.textContent = 'Please check the required consent boxes to continue.';
            errorMessage.style.display = 'block';
            submitButton.disabled = false;
            buttonText.textContent = 'Submit Application';
            loadingSpinner.style.display = 'none';
            return;
        }
        const disclosureHash = await this.computeConsentHash();

        try {
            let response;
            
            if (this.selectedFile) {
                const formData = new FormData();
                formData.append('firstName', document.getElementById('firstName').value);
                formData.append('lastName', document.getElementById('lastName').value);
                formData.append('email', document.getElementById('email').value);
                formData.append('phone', document.getElementById('phone').value);
                formData.append('specialty', document.getElementById('modal-specialty').value);
                formData.append('timeAvailable', document.getElementById('timeAvailable').value);
                formData.append('locationPreference', document.getElementById('locationPreference').value);
                formData.append('stateLicLocation', document.getElementById('stateLicLocation').value);
                formData.append('source', this.currentCTA);
                formData.append('createdAt', new Date().toISOString());
                formData.append('userAgent', navigator.userAgent);
                formData.append('health_collection_consent', healthCollectionConsent);
                formData.append('health_share_consent', healthShareConsent);
                formData.append('tcpa_contact_consent', tcpaContactConsent);
                formData.append('consent_version', this.consentVersion);
                formData.append('disclosure_hash', disclosureHash);
                formData.append('resume', this.selectedFile);
                
                if (this.jobId) {
                    formData.append('jobId', this.jobId);
                }
                
                response = await fetch(this.apiUrl, {
                    method: 'POST',
                    body: formData
                });
            } else {
                const jsonData = {
                    firstName: document.getElementById('firstName').value,
                    lastName: document.getElementById('lastName').value,
                    email: document.getElementById('email').value,
                    phone: document.getElementById('phone').value,
                    specialty: document.getElementById('modal-specialty').value,
                    timeAvailable: document.getElementById('timeAvailable').value,
                    locationPreference: document.getElementById('locationPreference').value,
                    stateLicLocation: document.getElementById('stateLicLocation').value,
                    source: this.currentCTA,
                    timestamp: new Date().toISOString(),
                    userAgent: navigator.userAgent,
                    health_collection_consent: healthCollectionConsent,
                    health_share_consent: healthShareConsent,
                    tcpa_contact_consent: tcpaContactConsent,
                    consent_version: this.consentVersion,
                    disclosure_hash: disclosureHash
                };
                
                if (this.jobId) {
                    jsonData.jobId = this.jobId;
                }
                
                response = await fetch(this.apiUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(jsonData)
                });
            }

            const result = await response.json();

            if (result.success) {
                successMessage.style.display = 'block';
                buttonText.textContent = 'Application Submitted!';
                
                setTimeout(() => {
                    this.close();
                }, 3000);
            } else {
                throw new Error(result.message || 'Failed to submit application');
            }
        } catch (error) {
            console.error('Error:', error);
            let errorText = 'There was an error submitting your information. Please try again.';
            
            if (error.message) {
                errorText = error.message;
            }
            
            errorMessage.textContent = `❌ ${errorText}`;
            errorMessage.style.display = 'block';
            buttonText.textContent = 'Try Again';
            submitButton.disabled = false;
        } finally {
            loadingSpinner.style.display = 'none';
        }
    }
}

/**
 * Simple Success Modal Class
 */
class SimpleSuccessModal {
    constructor() {
        this.modalHtml = `
            <div class="modal-overlay" id="successModalOverlay" style="display: none;">
                <div class="modal success-modal" id="successModal">
                    <button class="modal-close" type="button">&times;</button>
                    <div class="success-content">
                        <div class="success-icon">✅</div>
                        <h2 id="successModalTitle">Success!</h2>
                        <p id="successModalMessage"></p>
                        <button type="button" class="btn btn-primary" id="successModalClose">Close</button>
                    </div>
                </div>
            </div>
        `;
        this.init();
    }

    init() {
        document.body.insertAdjacentHTML('beforeend', this.modalHtml);
        this.modal = document.getElementById('successModal'); // Define modal reference
        this.setupEventListeners();
    }

    setupEventListeners() {
        // Close modal when clicking overlay
        document.getElementById('successModalOverlay').addEventListener('click', (e) => {
            if (e.target === e.currentTarget) {
                this.close();
            }
        });

        // Modal close buttons
        document.querySelector('#successModalOverlay .modal-close').addEventListener('click', () => {
            this.close();
        });
        
        document.getElementById('successModalClose').addEventListener('click', () => {
            this.close();
        });

        // Escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && document.getElementById('successModalOverlay').style.display !== 'none') {
                this.close();
            }
        });
    }

    open(message) {
        document.getElementById('successModalMessage').textContent = message;
        document.getElementById('successModalOverlay').style.display = 'flex';
        
        setTimeout(() => {
            this.modal.classList.add('active');
            // Focus the close button instead of firstName
            document.getElementById('successModalClose').focus();
        }, 10);
    }

    close() {
        document.getElementById('successModalOverlay').style.display = 'none';
        this.modal.classList.remove('active');

        setTimeout(() => {
            this.modal.style.display = 'none';
            // No form to reset for success modal
        }, 300);
    }
}

/**
 * Navigation Enhancements Class (Essential navigation only)
 */
class NavigationEnhancements {
    constructor() {
        this.init();
    }

    init() {
        this.setupMobileMenu();
        this.setupSmoothScrolling();
    }

    setupMobileMenu() {
        const mobileToggle = document.querySelector('.mobile-menu-toggle');
        if (mobileToggle) {
            mobileToggle.addEventListener('click', function() {
                const mainNav = document.querySelector('.main-nav');
                if (mainNav) {
                    mainNav.classList.toggle('active');
                }
            });
        }
    }

    setupSmoothScrolling() {
        const anchorLinks = document.querySelectorAll('a[href^="#"]');
        anchorLinks.forEach(link => {
            link.addEventListener('click', function(e) {
                e.preventDefault();
                const target = document.querySelector(this.getAttribute('href'));
                if (target) {
                    target.scrollIntoView({
                        behavior: 'smooth'
                    });
                }
            });
        });
    }
}

/**
 * Main Site Manager - Critical functions only
 */
class SiteManager {
    constructor() {
        this.functions = {};
        this.initializeFromConfig();
    }

    initializeFromConfig() {
        // Initialize cookie consent FIRST and directly
        this.functions.cookieConsent = new CookieConsent();
        
        // Then initialize other functions
        SITE_CONFIG.initFunctions.forEach(functionName => {
            try {
                switch(functionName) {
                    case 'simpleInfoModal':
                        this.functions.simpleInfoModal = new SimpleInfoModal();
                        break;
                    case 'professionalModal':
                        this.functions.professionalModal = new ProfessionalLeadModal();
                        break;
                    case 'successModal':
                        this.functions.successModal = new SimpleSuccessModal();
                    break;     
                    case 'navigationEnhancements':
                        this.functions.navigationEnhancements = new NavigationEnhancements();
                        break;
                    default:
                        if (SITE_CONFIG.features.debugMode) {
                            console.warn(`Function ${functionName} not found`);
                        }
                }
            } catch (error) {
                console.error(`Error initializing ${functionName}:`, error);
            }
        });
    }

    getFunction(name) {
        return this.functions[name];
    }

    isLoaded(name) {
        return name in this.functions;
    }
}

/**
 * Global helper functions
 */
window.openSimpleInfoModal = function(titleText) {
    if (window.SiteManager && window.SiteManager.functions.simpleInfoModal) {
        window.SiteManager.functions.simpleInfoModal.open(titleText);
    }
};

window.openProfessionalModal = function(titleText) {
    if (window.SiteManager && window.SiteManager.functions.professionalModal) {
        window.SiteManager.functions.professionalModal.open(titleText);
    }
};

window.openSuccessModal = function(message) {
    if (window.SiteManager && window.SiteManager.functions.successModal) {
        window.SiteManager.functions.successModal.open(message);
    }
};

// Helper function for job applications from Drupal nodes
window.handleJobApplicationFromNode = function(jobId, specialty, state, city, startDate) {
    const jobData = {
        jobId: jobId,
        specialty: specialty,
        location: state,
        city: city,
        startDate: startDate
    };
    
    const jobName = (specialty || "")
        .replace(/-/g, " ")
        .split(" ")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    
    const modalTitle = `Apply for ${jobName} in ${state}`;
    
    try {
        sessionStorage.setItem('currentJobApplication', JSON.stringify(jobData));
    } catch (e) {
        console.warn('Could not store job data in sessionStorage', e);
    }
    
    if (window.SiteManager && window.SiteManager.functions.professionalModal) {
        window.SiteManager.functions.professionalModal.open(modalTitle);
    }
};

/**
 * Initialize critical functions immediately
 * Since this script loads with defer, DOM is already parsed
 */
window.SiteManager = new SiteManager();

if (SITE_CONFIG.features.debugMode) {
    console.log('Critical functions initialized');
}